import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { SendHorizontalIcon, Loader2Icon } from 'lucide-react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Button } from '../ui/button';
import { MessageRow } from './MessageRow';
import { ToolCallHandlersContext, ChartAutoRunContext } from '../assistant-ui/chart-tool-call';
import type { ToolCallHandlers } from '../assistant-ui/chart-tool-call';
import { cn } from '@/lib/utils';

interface ThreadProps {
  runtime: any; // The runtime from useCustomLangGraphRuntime
  initialQuestion?: string;
  toolCallHandlers?: ToolCallHandlers;
  autoRunCharts?: boolean;
  emptyStateMessage?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: Array<{ type: 'text'; text: string } | { type: 'tool-call'; toolCallId: string; toolName: string; args: any; argsText: string; result?: string }>;
  status?: { type: 'incomplete'; reason: 'error'; error: Error };
}

type VirtualItem =
  | { type: 'empty'; key: string }
  | { type: 'message'; key: string; message: Message }
  | { type: 'loading'; key: string };

const DEFAULT_EMPTY_STATE_MESSAGE = "Hi, I'm Ellie. I'm here to help you analyze your business and make better decisions. What would you like to explore today?";

export const Thread: React.FC<ThreadProps> = ({
  runtime,
  initialQuestion,
  toolCallHandlers,
  autoRunCharts = true,
  emptyStateMessage = DEFAULT_EMPTY_STATE_MESSAGE,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isRunningRef = useRef(false);

  // Keep ref in sync so atBottomStateChange can read without stale closure
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  // Subscribe to runtime thread state
  useEffect(() => {
    const unsubscribe = runtime.thread.subscribe(() => {
      try {
        const state = runtime.thread.getState();
        if (!state) {
          return;
        }

        setIsRunning(state.isRunning || false);

        if (state.messages) {
          setMessages(state.messages as Message[]);
        }
      } catch (err) {
        console.debug('Thread state not ready:', err);
      }
    });

    // Get initial state
    try {
      const state = runtime.thread.getState();
      if (state) {
        setIsRunning(state.isRunning || false);
        if (state.messages) {
          setMessages(state.messages as Message[]);
        }
      }
    } catch (err) {
      console.debug('Initial thread state not ready:', err);
    }

    return () => unsubscribe?.();
  }, [runtime]);

  // Handle initial question
  useEffect(() => {
    if (initialQuestion) {
      runtime.thread.append({
        role: 'user',
        content: [{ type: 'text', text: initialQuestion }],
      });
    }
  }, [runtime, initialQuestion]);

  // Programmatic message sending (used by tool call components like file upload)
  const sendMessage = useCallback((text: string) => {
    if (!text.trim()) return;
    setShouldAutoScroll(true);
    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text }],
    });
  }, [runtime]);

  // Merge onSendMessage into tool call handlers
  const mergedToolCallHandlers = useMemo<ToolCallHandlers | undefined>(() => {
    return {
      ...toolCallHandlers,
      onSendMessage: sendMessage,
    };
  }, [toolCallHandlers, sendMessage]);

  // Handle sending a message
  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || isRunning) {
      return;
    }

    // Re-enable auto-scroll when user sends a new message
    setShouldAutoScroll(true);

    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text }],
    });

    setInputValue('');
    textareaRef.current?.focus();
  }, [inputValue, isRunning, runtime]);

  // Handle Enter key (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [inputValue]);

  // Build virtual items list
  const items = useMemo<VirtualItem[]>(() => {
    const result: VirtualItem[] = [];

    if (messages.length === 0) {
      result.push({ type: 'empty', key: 'empty-state' });
    }

    for (const msg of messages) {
      result.push({ type: 'message', key: msg.id, message: msg });
    }

    if (isRunning) {
      result.push({ type: 'loading', key: 'loading-indicator' });
    }

    return result;
  }, [messages, isRunning]);

  // Virtuoso callbacks
  const handleFollowOutput = useCallback(() => {
    return shouldAutoScroll ? 'smooth' as const : false as const;
  }, [shouldAutoScroll]);

  const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
    if (atBottom) {
      setShouldAutoScroll(true);
    } else if (!isRunningRef.current) {
      // Only disable if not actively streaming — smooth scroll briefly reports not-at-bottom
      setShouldAutoScroll(false);
    }
  }, []);

  const renderItem = useCallback((_index: number, item: VirtualItem) => {
    if (item.type === 'empty') {
      return null;
    }

    if (item.type === 'loading') {
      return (
        <div className="flex justify-start mb-4 px-16">
          <div className="max-w-[80%]">
            <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        </div>
      );
    }

    return (
      <div className="px-16 py-2">
        <MessageRow message={item.message} />
      </div>
    );
  }, [emptyStateMessage]);

  return (
    <ToolCallHandlersContext.Provider value={mergedToolCallHandlers}>
      <ChartAutoRunContext.Provider value={autoRunCharts}>
        <div className="h-full flex flex-col">
          <Virtuoso
            ref={virtuosoRef}
            className="flex-1"
            data={items}
            computeItemKey={(_index, item) => item.key}
            initialTopMostItemIndex={Math.max(0, items.length - 1)}
            followOutput={handleFollowOutput}
            atBottomStateChange={handleAtBottomStateChange}
            atBottomThreshold={100}
            increaseViewportBy={{ top: 400, bottom: 400 }}
            itemContent={renderItem}
            components={{
              Header: () => <div className="h-12" />,
              Footer: () => <div className="h-4" />,
            }}
          />

          <div className="border-t p-4">
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write a message..."
                disabled={isRunning}
                rows={1}
                className={cn(
                  "flex min-h-[2.5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden"
                )}
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || isRunning}
                size="icon"
                className="h-10 w-10 shrink-0"
              >
                {isRunning ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <SendHorizontalIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </ChartAutoRunContext.Provider>
    </ToolCallHandlersContext.Provider>
  );
};
